import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import {
  FiPackage, FiTruck, FiPrinter, FiFileText, FiX, FiRefreshCw,
  FiMapPin, FiDollarSign, FiSearch, FiFilter, FiEye, FiCalendar,
  FiCheckCircle, FiClock, FiAlertCircle
} from 'react-icons/fi';
import '../styles/AdminShipments.css';

const AdminShipments = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walletBalance, setWalletBalance] = useState(null);
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [trackingModal, setTrackingModal] = useState({ open: false, data: null });
  const [detailsModal, setDetailsModal] = useState({ open: false, data: null });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchShipments();
    fetchWalletBalance();
    fetchPickupLocations();
  }, [statusFilter, currentPage]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
  });

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', currentPage);
      params.append('per_page', 20);

      const response = await apiClient.get(`/admin/shipments?${params.toString()}`, getAuthHeaders());
      setShipments(response.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      setError('Failed to fetch shipments');
      setLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const response = await apiClient.get('/admin/shipments/wallet/balance', getAuthHeaders());
      setWalletBalance(response.data);
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
    }
  };

  const fetchPickupLocations = async () => {
    try {
      const response = await apiClient.get('/admin/shipments/pickup-locations', getAuthHeaders());
      setPickupLocations(response.data.data?.shipping_address || []);
    } catch (error) {
      console.error('Error fetching pickup locations:', error);
    }
  };

  const handleTrackShipment = async (shipmentId) => {
    try {
      setActionLoading(true);
      const response = await apiClient.get(`/admin/shipments/${shipmentId}/track`, getAuthHeaders());
      setTrackingModal({ open: true, data: response.data });
      setActionLoading(false);
    } catch (error) {
      console.error('Error tracking shipment:', error);
      toast.error('Failed to track shipment');
      setActionLoading(false);
    }
  };

  const handleViewDetails = async (orderId) => {
    try {
      setActionLoading(true);
      const response = await apiClient.get(`/admin/shipments/${orderId}`, getAuthHeaders());
      setDetailsModal({ open: true, data: response.data });
      setActionLoading(false);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast.error('Failed to fetch shipment details');
      setActionLoading(false);
    }
  };

  const handleGenerateLabel = async (shipmentId) => {
    try {
      setActionLoading(true);
      const response = await apiClient.post(`/admin/shipments/${shipmentId}/label`, {}, getAuthHeaders());
      if (response.data.labelUrl) {
        window.open(response.data.labelUrl, '_blank');
        toast.success('Label generated successfully!');
      } else {
        toast.error('Label URL not available');
      }
      setActionLoading(false);
    } catch (error) {
      console.error('Error generating label:', error);
      toast.error('Failed to generate label');
      setActionLoading(false);
    }
  };

  const handleSchedulePickup = async (shipmentId) => {
    setConfirmModal({
      open: true,
      title: 'Schedule Pickup',
      message: 'Are you sure you want to schedule pickup for this shipment? Our courier partner will arrive at your pickup location.',
      type: 'primary',
      confirmText: 'Schedule',
      onConfirm: async () => {
        setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        try {
          setActionLoading(true);
          await apiClient.post(`/admin/shipments/${shipmentId}/pickup`, {}, getAuthHeaders());
          toast.success('Pickup scheduled successfully!');
          fetchShipments();
          setActionLoading(false);
        } catch (error) {
          console.error('Error scheduling pickup:', error);
          toast.error('Failed to schedule pickup');
          setActionLoading(false);
        }
      }
    });
  };

  const handleCancelShipments = async () => {
    if (selectedShipments.length === 0) {
      toast.warning('Please select shipments to cancel');
      return;
    }

    setConfirmModal({
      open: true,
      title: 'Cancel Shipments',
      message: `Are you sure you want to cancel ${selectedShipments.length} shipment(s)? This will cancel both the shipment and the order.`,
      type: 'danger',
      confirmText: 'Cancel Shipments',
      onConfirm: async () => {
        setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
        try {
          setActionLoading(true);

          // For each selected shipment, cancel on Shiprocket and in database
          for (const shipmentId of selectedShipments) {
            // Find the corresponding order to get channel_order_id
            const shipment = shipments.find(s => s.id === shipmentId);
            const channelOrderId = shipment?.channel_order_id;

            await apiClient.post('/admin/shipments/cancel', {
              shipmentIds: [shipmentId],
              channelOrderId: channelOrderId
            }, getAuthHeaders());
          }

          toast.success('Shipments and orders cancelled successfully!');
          setSelectedShipments([]);
          fetchShipments();
          fetchWalletBalance();
          setActionLoading(false);
        } catch (error) {
          console.error('Error cancelling shipments:', error);
          toast.error('Failed to cancel shipments');
          setActionLoading(false);
        }
      }
    });
  };

  const handleDownloadInvoice = async (orderId) => {
    try {
      setActionLoading(true);
      const response = await apiClient.post('/admin/shipments/invoice',
        { orderIds: [orderId] },
        getAuthHeaders()
      );
      if (response.data.invoice_url) {
        window.open(response.data.invoice_url, '_blank');
        toast.success('Invoice downloaded!');
      } else {
        toast.error('Invoice URL not available');
      }
      setActionLoading(false);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
      setActionLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (selectedShipments.length === 0) {
      toast.warning('Please select orders to generate invoice');
      return;
    }
    try {
      setActionLoading(true);
      const response = await apiClient.post('/admin/shipments/invoice',
        { orderIds: selectedShipments },
        getAuthHeaders()
      );
      if (response.data.invoice_url) {
        window.open(response.data.invoice_url, '_blank');
        toast.success('Invoice generated successfully!');
      }
      setActionLoading(false);
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
      setActionLoading(false);
    }
  };

  const handleGenerateManifest = async () => {
    if (selectedShipments.length === 0) {
      toast.warning('Please select shipments to generate manifest');
      return;
    }
    try {
      setActionLoading(true);
      const response = await apiClient.post('/admin/shipments/manifest',
        { shipmentIds: selectedShipments },
        getAuthHeaders()
      );
      if (response.data.manifest_url) {
        window.open(response.data.manifest_url, '_blank');
        toast.success('Manifest generated successfully!');
      }
      setActionLoading(false);
    } catch (error) {
      console.error('Error generating manifest:', error);
      toast.error('Failed to generate manifest');
      setActionLoading(false);
    }
  };

  const toggleSelectShipment = (id) => {
    setSelectedShipments(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedShipments.length === shipments.length) {
      setSelectedShipments([]);
    } else {
      setSelectedShipments(shipments.map(s => s.id));
    }
  };

  const getStatusBadge = (status, statusCode) => {
    // First check text status for cancelled/delivered - these are definitive
    const normalizedStatus = status?.toUpperCase()?.replace('_', ' ');

    // Priority statuses that should override status_code
    const priorityStatusMap = {
      'CANCELED': { label: 'Cancelled', color: '#f44336' },
      'CANCELLED': { label: 'Cancelled', color: '#f44336' },
      'DELIVERED': { label: 'Delivered', color: '#4caf50' },
      'RTO': { label: 'RTO', color: '#ff5722' },
      'RTO INITIATED': { label: 'RTO Initiated', color: '#ff5722' },
      'RTO DELIVERED': { label: 'RTO Delivered', color: '#ff5722' },
    };

    if (normalizedStatus && priorityStatusMap[normalizedStatus]) {
      const config = priorityStatusMap[normalizedStatus];
      return (
        <span className="status-badge" style={{ backgroundColor: config.color }}>
          {config.label}
        </span>
      );
    }

    // Shiprocket status codes mapping for other statuses
    const statusCodeMap = {
      1: { label: 'AWB Assigned', color: '#2196f3' },
      2: { label: 'Label Generated', color: '#9c27b0' },
      3: { label: 'Pickup Scheduled', color: '#ff9800' },
      4: { label: 'Pickup Queued', color: '#ff9800' },
      5: { label: 'Manifest Generated', color: '#00bcd4' },
      6: { label: 'Shipped', color: '#3f51b5' },
      7: { label: 'Delivered', color: '#4caf50' },
      8: { label: 'Cancelled', color: '#f44336' },
      9: { label: 'RTO Initiated', color: '#ff5722' },
      10: { label: 'RTO Delivered', color: '#ff5722' },
      11: { label: 'Pending', color: '#757575' },
      12: { label: 'Lost', color: '#d32f2f' },
      13: { label: 'Pickup Error', color: '#f44336' },
      14: { label: 'RTO Acknowledged', color: '#ff5722' },
      15: { label: 'RTO In Transit', color: '#ff5722' },
      16: { label: 'RTO Not Delivered', color: '#ff5722' },
      17: { label: 'RTO OFD', color: '#ff5722' },
      18: { label: 'Pickup Rescheduled', color: '#ff9800' },
      19: { label: 'Undelivered', color: '#f44336' },
      20: { label: 'In Transit', color: '#3f51b5' },
      21: { label: 'Out for Delivery', color: '#8bc34a' },
      22: { label: 'Out for Pickup', color: '#ff9800' },
      23: { label: 'Pickup Exception', color: '#f44336' },
      24: { label: 'Undelivered Returned', color: '#ff5722' },
    };

    // Try to match by status code
    if (statusCode && statusCodeMap[statusCode]) {
      const config = statusCodeMap[statusCode];
      return (
        <span className="status-badge" style={{ backgroundColor: config.color }}>
          {config.label}
        </span>
      );
    }

    // Fallback: Map text-based status
    const statusTextMap = {
      'NEW': { label: 'New', color: '#2196f3' },
      'PENDING': { label: 'Pending', color: '#ff9800' },
      'PICKUP SCHEDULED': { label: 'Pickup Scheduled', color: '#9c27b0' },
      'PICKED UP': { label: 'Picked Up', color: '#00bcd4' },
      'IN TRANSIT': { label: 'In Transit', color: '#3f51b5' },
      'OUT FOR DELIVERY': { label: 'Out for Delivery', color: '#8bc34a' },
    };

    const config = statusTextMap[normalizedStatus] || { label: status || 'Unknown', color: '#757575' };
    return (
      <span className="status-badge" style={{ backgroundColor: config.color }}>
        {config.label}
      </span>
    );
  };

  const filteredShipments = shipments.filter(shipment => {
    // First, apply client-side status filter to handle Shiprocket API quirks
    // (cancelled orders may be returned by status_id filters due to their previous status_code)
    if (statusFilter) {
      const status = (shipment.status || '').toUpperCase();
      const statusCode = shipment.status_code;

      // Map filter values to expected statuses
      const filterMap = {
        '1': { codes: [1], excludeStatus: ['CANCELED', 'CANCELLED'] },      // New/Pending
        '2': { codes: [2], excludeStatus: ['CANCELED', 'CANCELLED'] },      // AWB Assigned
        '4': { codes: [4], excludeStatus: ['CANCELED', 'CANCELLED'] },      // Pickup Scheduled
        '5': { codes: [5], excludeStatus: ['CANCELED', 'CANCELLED'] },      // Picked Up
        '6': { codes: [6], excludeStatus: ['CANCELED', 'CANCELLED'] },      // In Transit
        '7': { statuses: ['DELIVERED'], codes: [7] },                        // Delivered - must have DELIVERED status
        '8': { statuses: ['CANCELED', 'CANCELLED'] },                        // Cancelled - must have CANCELED status
        '9': { statuses: ['RTO INITIATED', 'RTO'], codes: [9] },            // RTO Initiated
        '10': { statuses: ['RTO DELIVERED'], codes: [10] },                 // RTO Delivered
        '17': { codes: [17], excludeStatus: ['CANCELED', 'CANCELLED'] },    // Ready to Ship
        '18': { codes: [18], excludeStatus: ['CANCELED', 'CANCELLED'] },    // Out for Delivery
      };

      const filter = filterMap[statusFilter];
      if (filter) {
        // If filter requires specific status text (like DELIVERED, CANCELED)
        if (filter.statuses) {
          if (!filter.statuses.some(s => status.includes(s))) {
            return false;
          }
        }
        // If filter should exclude certain statuses (like cancelled)
        if (filter.excludeStatus) {
          if (filter.excludeStatus.some(s => status.includes(s))) {
            return false;
          }
        }
      }
    }

    // Then apply search query filter
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      shipment.channel_order_id?.toLowerCase().includes(query) ||
      shipment.customer_name?.toLowerCase().includes(query) ||
      shipment.awb_code?.toLowerCase().includes(query)
    );
  });

  if (loading && shipments.length === 0) {
    return <div className="admin-shipments loading">Loading shipments...</div>;
  }

  return (
    <div className="admin-shipments">
      <div className="shipments-header">
        <h1><FiTruck /> Shipment Management</h1>
        <div className="header-stats">
          {walletBalance && (
            <div className="wallet-balance">
              <span>Wallet: ₹{parseFloat(walletBalance.data?.balance_amount || 0).toFixed(2)}</span>
            </div>
          )}
          <button className="refresh-btn" onClick={() => { fetchShipments(); fetchWalletBalance(); }}>
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Pickup Locations */}
      {pickupLocations.length > 0 && (
        <div className="pickup-locations-bar">
          <FiMapPin /> <strong>Pickup Locations:</strong>
          {pickupLocations.map((loc, idx) => (
            <span key={idx} className="pickup-tag">
              {loc.pickup_location} ({loc.city})
            </span>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="shipments-controls">
        <div className="search-filter">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search by Order ID, Customer, AWB..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className={`filter-box ${statusFilter ? 'active' : ''}`}>
            <FiFilter className="filter-icon" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="status-filter-select"
            >
              <option value="">All Status</option>
              <optgroup label="Processing">
                <option value="1">New / Pending</option>
                <option value="17">Ready to Ship</option>
                <option value="2">AWB Assigned</option>
                <option value="4">Pickup Scheduled</option>
              </optgroup>
              <optgroup label="In Transit">
                <option value="5">Picked Up</option>
                <option value="6">Shipped / In Transit</option>
                <option value="18">Out for Delivery</option>
              </optgroup>
              <optgroup label="Completed">
                <option value="7">Delivered</option>
                <option value="8">Cancelled</option>
              </optgroup>
              <optgroup label="Returns">
                <option value="9">RTO Initiated</option>
                <option value="10">RTO Delivered</option>
              </optgroup>
            </select>
            {statusFilter && (
              <button
                className="clear-filter-btn"
                onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
                title="Clear filter"
              >
                <FiX />
              </button>
            )}
          </div>
        </div>

        <div className="bulk-actions">
          {selectedShipments.length > 0 && (
            <>
              <span className="selected-count">{selectedShipments.length} selected</span>
              <button className="action-btn danger" onClick={handleCancelShipments}>
                <FiX /> Cancel
              </button>
              <button className="action-btn" onClick={handleGenerateInvoice}>
                <FiFileText /> Invoice
              </button>
              <button className="action-btn" onClick={handleGenerateManifest}>
                <FiPackage /> Manifest
              </button>
            </>
          )}
        </div>
      </div>

      {/* Quick Filter Chips */}
      <div className="quick-filter-chips">
        <span className="chip-label">Quick filters:</span>
        <button
          className={`filter-chip ${statusFilter === '' ? 'active' : ''}`}
          onClick={() => { setStatusFilter(''); setCurrentPage(1); }}
        >
          All
        </button>
        <button
          className={`filter-chip pending ${statusFilter === '1' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('1'); setCurrentPage(1); }}
        >
          New
        </button>
        <button
          className={`filter-chip processing ${statusFilter === '17' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('17'); setCurrentPage(1); }}
        >
          Ready to Ship
        </button>
        <button
          className={`filter-chip shipped ${statusFilter === '6' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('6'); setCurrentPage(1); }}
        >
          In Transit
        </button>
        <button
          className={`filter-chip delivered ${statusFilter === '7' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('7'); setCurrentPage(1); }}
        >
          Delivered
        </button>
        <button
          className={`filter-chip cancelled ${statusFilter === '8' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('8'); setCurrentPage(1); }}
        >
          Cancelled
        </button>
        <button
          className={`filter-chip rto ${statusFilter === '9' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('9'); setCurrentPage(1); }}
        >
          RTO
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Shipments Table */}
      <div className="shipments-table-wrapper">
        <table className="shipments-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedShipments.length === shipments.length && shipments.length > 0}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Order ID</th>
              <th>Customer</th>
              <th>AWB Code</th>
              <th>Courier</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredShipments.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  No shipments found. Orders will appear here after successful payment.
                </td>
              </tr>
            ) : (
              filteredShipments.map((shipment) => {
                // Extract data from nested shipments array
                const shipmentData = shipment.shipments?.[0] || {};
                const awbCode = shipmentData.awb || shipment.awb_code;
                const courierName = shipmentData.courier || shipment.courier_name;
                // Use shipment level status (more accurate for cancelled orders)
                const displayStatus = shipment.status;
                const displayStatusCode = shipment.status_code;

                return (
                  <tr key={shipment.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedShipments.includes(shipment.id)}
                        onChange={() => toggleSelectShipment(shipment.id)}
                      />
                    </td>
                    <td>
                      <strong>{shipment.channel_order_id}</strong>
                      <br />
                      <small>#{shipment.id}</small>
                    </td>
                    <td>
                      {shipment.customer_name}
                      <br />
                      <small>{shipment.customer_phone}</small>
                    </td>
                    <td>
                      {awbCode || <span className="pending-text">Pending</span>}
                    </td>
                    <td>{courierName || '-'}</td>
                    <td>{getStatusBadge(displayStatus, displayStatusCode)}</td>
                    <td>₹{shipment.total || '0'}</td>
                    <td>{new Date(shipment.created_at).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="icon-btn"
                          title="View Details"
                          onClick={() => handleViewDetails(shipment.id)}
                        >
                          <FiEye />
                        </button>
                        {shipment.shipments?.[0]?.id && (
                          <>
                            <button
                              className="icon-btn"
                              title="Track Shipment"
                              onClick={() => handleTrackShipment(shipment.shipments[0].id)}
                            >
                              <FiMapPin />
                            </button>
                            <button
                              className="icon-btn"
                              title="Print Label"
                              onClick={() => handleGenerateLabel(shipment.shipments[0].id)}
                            >
                              <FiPrinter />
                            </button>
                            <button
                              className="icon-btn"
                              title="Schedule Pickup"
                              onClick={() => handleSchedulePickup(shipment.shipments[0].id)}
                            >
                              <FiCalendar />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
        >
          Previous
        </button>
        <span>Page {currentPage}</span>
        <button onClick={() => setCurrentPage(p => p + 1)}>
          Next
        </button>
      </div>

      {/* Tracking Modal */}
      {trackingModal.open && (
        <div className="modal-overlay" onClick={() => setTrackingModal({ open: false, data: null })}>
          <div className="modal-content tracking-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiMapPin /> Shipment Tracking</h2>
              <button className="close-btn" onClick={() => setTrackingModal({ open: false, data: null })}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {trackingModal.data?.tracking_data ? (() => {
                const trackData = trackingModal.data.tracking_data;
                const shipmentTrack = trackData.shipment_track?.[0] || {};
                const currentStatus = shipmentTrack.current_status || 'Unknown';
                const isDelivered = currentStatus.toLowerCase().includes('delivered');
                const isCancelled = currentStatus.toLowerCase().includes('cancel');

                return (
                  <div className="tracking-info">
                    {/* Current Status Card */}
                    <div className={`tracking-status-card ${isDelivered ? 'delivered' : isCancelled ? 'cancelled' : 'in-progress'}`}>
                      <div className="status-icon-large">
                        {isDelivered ? <FiCheckCircle /> : <FiTruck />}
                      </div>
                      <div className="status-details">
                        <span className="status-text">{currentStatus}</span>
                        {trackData.etd && !isDelivered && !isCancelled && (
                          <span className="etd-text">
                            Expected by {new Date(trackData.etd).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* AWB & Courier Info */}
                    <div className="tracking-quick-info">
                      <div className="quick-info-item">
                        <FiPackage className="info-icon" />
                        <div>
                          <span className="info-label">AWB Number</span>
                          <span className="info-value">{shipmentTrack.awb_code || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="quick-info-item">
                        <FiTruck className="info-icon" />
                        <div>
                          <span className="info-label">Courier Partner</span>
                          <span className="info-value">{shipmentTrack.courier_name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    {trackData.track_url && (
                      <a href={trackData.track_url} target="_blank" rel="noopener noreferrer" className="track-link">
                        <FiMapPin /> Track on Courier Website
                      </a>
                    )}

                    {/* Activity Timeline */}
                    {trackData.shipment_track_activities && trackData.shipment_track_activities.length > 0 && (
                      <div className="tracking-timeline">
                        <h3><FiClock /> Shipment Activity</h3>
                        <div className="timeline-container">
                          {trackData.shipment_track_activities.map((activity, idx) => (
                            <div key={idx} className={`timeline-item ${idx === 0 ? 'latest' : ''}`}>
                              <div className="timeline-marker">
                                <div className="timeline-dot"></div>
                                {idx < trackData.shipment_track_activities.length - 1 && <div className="timeline-line"></div>}
                              </div>
                              <div className="timeline-content">
                                <p className="activity-status">{activity.activity}</p>
                                <div className="activity-meta">
                                  {activity.location && <span className="activity-location"><FiMapPin /> {activity.location}</span>}
                                  <span className="activity-date"><FiCalendar /> {activity.date}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })() : (
                <div className="no-tracking-data">
                  <FiAlertCircle className="no-data-icon" />
                  <p>No tracking data available for this shipment.</p>
                  <span>Tracking information will appear once the shipment is picked up.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsModal.open && (
        <div className="modal-overlay" onClick={() => setDetailsModal({ open: false, data: null })}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiPackage /> Order Details</h2>
              <button className="close-btn" onClick={() => setDetailsModal({ open: false, data: null })}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              {detailsModal.data?.data ? (() => {
                const order = detailsModal.data.data;
                // Handle shipments as object or array
                const shipment = Array.isArray(order.shipments)
                  ? (order.shipments[0] || {})
                  : (order.shipments || {});
                const others = order.others || {};
                const pickupAddress = order.pickup_address_detail || {};
                return (
                  <div className="order-details">
                    {/* Status Banner */}
                    <div className={`order-status-banner ${order.status?.toLowerCase()}`}>
                      <span className="status-icon">
                        {order.status === 'CANCELED' ? <FiX /> : order.status === 'DELIVERED' ? <FiCheckCircle /> : <FiTruck />}
                      </span>
                      <div className="status-info">
                        <span className="status-label">Order Status</span>
                        <span className="status-value">{order.status}</span>
                      </div>
                      {shipment.awb && (
                        <div className="awb-info">
                          <span className="awb-label">AWB</span>
                          <span className="awb-value">{shipment.awb}</span>
                        </div>
                      )}
                    </div>

                    <div className="details-grid">
                      <div className="detail-section">
                        <h3><FiFileText /> Order Information</h3>
                        <div className="detail-row">
                          <span className="label">Order ID</span>
                          <span className="value">{order.channel_order_id}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Created</span>
                          <span className="value">{order.created_at}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Payment</span>
                          <span className="value payment-badge">{order.payment_method?.toUpperCase()}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Total</span>
                          <span className="value amount">₹{order.total}</span>
                        </div>
                        {order.invoice_no && (
                          <div className="detail-row">
                            <span className="label">Invoice</span>
                            <span className="value">{order.invoice_no}</span>
                          </div>
                        )}
                      </div>

                      <div className="detail-section">
                        <h3><FiMapPin /> Customer Details</h3>
                        <div className="detail-row">
                          <span className="label">Name</span>
                          <span className="value">{order.customer_name}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Phone</span>
                          <span className="value">{order.customer_phone}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Email</span>
                          <span className="value">{order.customer_email}</span>
                        </div>
                        <div className="detail-row address">
                          <span className="label">Address</span>
                          <span className="value">
                            {order.customer_address}<br />
                            {order.customer_city}, {order.customer_state} - {order.customer_pincode}
                          </span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3><FiPackage /> Package Details</h3>
                        <div className="detail-row">
                          <span className="label">Weight</span>
                          <span className="value">{shipment.weight || others.weight || 'N/A'} kg</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Dimensions</span>
                          <span className="value">{shipment.dimensions || others.dimensions || 'N/A'}</span>
                        </div>
                        <div className="detail-row address">
                          <span className="label">Pickup Location</span>
                          <span className="value">
                            {pickupAddress.address ? (
                              <>
                                {pickupAddress.address}{pickupAddress.address_2 ? `, ${pickupAddress.address_2}` : ''}<br />
                                {pickupAddress.city}, {pickupAddress.state}, {pickupAddress.pin_code}
                              </>
                            ) : order.pickup_location || 'N/A'}
                          </span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h3><FiTruck /> Shipping Details</h3>
                        <div className="detail-row">
                          <span className="label">Courier</span>
                          <span className="value">{shipment.courier || 'Not assigned'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">AWB</span>
                          <span className="value courier-awb">{shipment.awb || 'Not assigned'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Shipment Status</span>
                          <span className="value">{shipment.status || 'Pending'}</span>
                        </div>
                        {shipment.etd && (
                          <div className="detail-row">
                            <span className="label">Expected Delivery</span>
                            <span className="value">{shipment.etd}</span>
                          </div>
                        )}
                        {shipment.pickup_scheduled_date && (
                          <div className="detail-row">
                            <span className="label">Pickup Scheduled</span>
                            <span className="value">{shipment.pickup_scheduled_date}</span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span className="label">Zone</span>
                          <span className="value">{order.zone?.toUpperCase() || 'N/A'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="label">Invoice</span>
                          <button
                            className="invoice-btn"
                            onClick={() => handleDownloadInvoice(order.id)}
                            disabled={actionLoading}
                          >
                            {actionLoading ? 'Generating...' : 'Download Invoice'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="items-section">
                      <h3><FiPackage /> Order Items ({order.products?.length || 0})</h3>
                      <table className="items-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>SKU</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.products?.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.name}</td>
                              <td><code>{item.channel_sku || item.sku}</code></td>
                              <td>{item.quantity}</td>
                              <td>₹{item.selling_price}</td>
                              <td>₹{(item.selling_price * item.quantity).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {order.activities && order.activities.length > 0 && (
                      <div className="activity-section">
                        <h3><FiClock /> Order Activity</h3>
                        <div className="activity-tags">
                          {order.activities.map((activity, idx) => (
                            <span key={idx} className="activity-tag">{activity.replace(/_/g, ' ')}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })() : (
                <p className="no-data-text">No order details available.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {actionLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Processing...</p>
        </div>
      )}

      {/* Confirm Modal */}
      {confirmModal.open && (
        <div className="modal-overlay" onClick={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}>
          <div className="modal-content confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">
              {confirmModal.type === 'danger' ? (
                <FiAlertCircle className="icon-danger" />
              ) : (
                <FiTruck className="icon-primary" />
              )}
            </div>
            <h2 className="confirm-title">{confirmModal.title}</h2>
            <p className="confirm-message">{confirmModal.message}</p>
            <div className="confirm-actions">
              <button
                className="btn-cancel"
                onClick={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
              >
                No, Cancel
              </button>
              <button
                className={`btn-confirm ${confirmModal.type === 'danger' ? 'btn-danger' : ''}`}
                onClick={confirmModal.onConfirm}
              >
                Yes, {confirmModal.confirmText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShipments;
